import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const HowToSeeUnfollowers: React.FC = () => {
  useSEO(
    'How to See Who Doesn\'t Follow You Back on Instagram — Purgr',
    'Find out who doesn\'t follow you back on Instagram without logging in or using sketchy apps. Uses your official Instagram data export — 100% private.'
  );

  return (
    <PageLayout title="How to See Who Doesn't Follow You Back on Instagram">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">
        <p className="text-white/80 text-base">
          Wondering who you follow that doesn't follow you back? It's one of the most common
          Instagram questions — and it's surprisingly hard to answer manually. Here's everything
          you need to know.
        </p>

        {/* Why it's hard manually */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">Why you can't do this manually</p>
          <p className="text-white/55">
            Instagram doesn't show you a "not following back" list anywhere in the app. To find
            out manually, you'd have to open every single profile you follow and check if they
            follow you back — which is impossible if you follow more than a handful of people.
            Even at 200 follows, that's hours of clicking. At 1,000+, it's completely impractical.
          </p>
        </div>

        {/* Warning about sketchy apps */}
        <div className="glass-card p-5 border border-yellow-500/20">
          <p className="text-white/90 font-semibold mb-2">⚠️ Avoid apps that ask for your Instagram login</p>
          <p className="text-white/55">
            Many "unfollow tracker" apps ask for your Instagram username and password. This violates
            Instagram's Terms of Service and puts your account at serious risk — your account can be
            banned, and your credentials can be stolen. Purgr never asks for your login.
          </p>
        </div>

        {/* How Purgr works */}
        <div>
          <p className="text-white/90 font-bold text-base mb-4">How to check with Purgr (the safe way)</p>
          <div className="space-y-4">
            {[
              {
                n: '1',
                title: 'Download your Instagram data',
                body: 'Request your data export from Instagram Settings → Account → Download your information. Select JSON format and the "Followers and following" category.',
                link: { to: '/how-to-download-instagram-data', label: 'Full download guide →' },
              },
              {
                n: '2',
                title: 'Upload following.json',
                body: 'On the Purgr home page, upload the following.json file from your data export. This is the list of accounts you follow.',
              },
              {
                n: '3',
                title: 'Upload followers_1.json',
                body: 'Upload your followers_1.json file. This is the list of accounts that follow you back.',
              },
              {
                n: '4',
                title: 'Hit "Compare Now" and see your results',
                body: 'Purgr instantly shows you three lists: who\'s not following you back, who you\'re not following back, and your mutuals. All processed locally — nothing is ever sent to a server.',
              },
            ].map(({ n, title, body, link }) => (
              <div key={n} className="glass-card p-5 flex gap-4">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <div>
                  <p className="text-white/90 font-semibold mb-1">{title}</p>
                  <p className="text-white/55">{body}</p>
                  {link && (
                    <Link to={link.to} className="text-white/40 hover:text-white/70 text-xs mt-1 inline-block transition-colors">
                      {link.label}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What to do with results */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">What to do with the results</p>
          <p className="text-white/55 mb-3">
            Once you have your "not following back" list, you have a few options:
          </p>
          <ul className="space-y-2 text-white/55">
            <li>• <strong className="text-white/70">Unfollow in batches</strong> — Instagram may temporarily restrict your account if you unfollow too many people too quickly. Keep it to ~50–100 per day.</li>
            <li>• <strong className="text-white/70">Check the profiles first</strong> — some accounts you follow might be worth keeping even if they don't follow back (brands, creators, news accounts).</li>
            <li>• <strong className="text-white/70">Copy the list</strong> — use Purgr's "Copy to clipboard" or "Download CSV" buttons to save the list for reference.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">See your non-followers now</p>
            <p className="text-white/50 text-sm mb-4">Free, private, no Instagram login needed.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-download-instagram-data" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to download your Instagram data →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HowToSeeUnfollowers;
